import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { fetchFaq } from '../api/public'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { schemaBreadcrumb, schemaFaqPage, schemaGraph, schemaWebPage } from '../lib/schema-org'

/** Standalone FAQ URL for SEO; primary FAQ also appears on /about. */
export function FaqPage() {
  useCrtPageTitle('FAQ')
  const q = useQuery({
    queryKey: ['public-faq'],
    queryFn: fetchFaq,
  })

  const items = q.data ? [...q.data.faq].sort((a, b) => a.sortOrder - b.sortOrder) : []

  const jsonLd = useMemo(() => {
    const crumbs = schemaBreadcrumb(
      [
        { name: 'Home', path: '/' },
        { name: 'FAQ', path: '/faq' },
      ],
      '/faq',
    )
    const faq = schemaFaqPage(items)
    return schemaGraph(
      crumbs,
      schemaWebPage({
        path: '/faq',
        name: 'FAQ — BNKR',
        description: 'Häufig gestellte Fragen zu BNKR Events, Label und Verein.',
        breadcrumb: crumbs,
        mainEntity: { '@id': faq['@id'] as string },
      }),
      faq,
    )
  }, [items])

  usePageMeta({
    title: 'FAQ',
    description: 'FAQ zu BNKR — Events, Tickets, Label und Verein.',
    canonical: '/faq',
    jsonLd,
  })

  return (
    <section className="section" style={{ paddingBottom: 'var(--sp-16)', paddingTop: 'var(--sp-4)' }}>
      <div className="wrap">
        <section id="faq">
          <p className="muted" style={{ marginTop: 0, marginBottom: 24 }}>
            Die gleichen Antworten findest du auch auf der{' '}
            <Link to="/about">Über-uns</Link>-Seite.
          </p>
          <div className="kicker crt">↳ /faq</div>
          <h1 className="display h2" style={{ marginTop: 14 }}>
            FAQ
          </h1>

          {q.isError && <p className="error">FAQ konnte nicht geladen werden.</p>}
          {q.data && items.length === 0 && <p className="muted">Noch keine Einträge.</p>}
          <div className="faq-list" style={{ marginTop: 48 }}>
            {items.map((item) => (
              <details key={item.id} className="faq-item">
                <summary className="faq-q">
                  {item.question} <span className="plus">+</span>
                </summary>
                <div className="faq-a rich-text rich-text--paper" dangerouslySetInnerHTML={{ __html: item.answerHtml }} />
              </details>
            ))}
          </div>
        </section>
      </div>
    </section>
  )
}
